export enum LoginErrorKind {
  UserNotFound = 'USER-NOT-FOUND',
  ApiUnreachable = 'API-UNREACHABLE',
  SignInRejected = 'SIGN-IN-REJECTED',
  ServerError = 'SERVER-ERROR',
  Unknown = 'UNKNOWN',
}

export interface ClassifiedLoginError {
  kind: LoginErrorKind;
  code: string;
  statusCode?: number;
  detail?: string;
}

interface ErrorMessage {
  message: string;
}

// The parts of an ApolloError the classifier reads. Errors thrown outside Apollo have none of them and fall through to Unknown.
interface LoginErrorShape {
  message?: string;
  graphQLErrors?: readonly ErrorMessage[];
  networkError?: {
    statusCode?: number;
    result?: { errors?: ErrorMessage[] };
  } | null;
}

// ezpay-api-svc throws FORBIDDEN [4] (no user context) while building the request context when the signed-in email
// matches no merchant Person, or matches one with no tenant relationship. [3] is only thrown on the API-key path.
const userNotFoundPatterns = [/FORBIDDEN \[4\]/, /Person document with specified email is not found/];

export const classifyLoginError = (error: unknown, queryErrorMessages: string[] = []): ClassifiedLoginError => {
  const shape = (error ?? {}) as LoginErrorShape;
  const { networkError } = shape;
  const statusCode = networkError?.statusCode;
  const messages = [
    ...queryErrorMessages,
    ...(networkError?.result?.errors ?? []).map(e => e.message),
    ...(shape.graphQLErrors ?? []).map(e => e.message),
  ].filter(m => !!m);
  const detail = messages[0] ?? shape.message;

  const classified = (kind: LoginErrorKind): ClassifiedLoginError => ({ kind, code: `MP-LOGIN-${kind}`, statusCode, detail });

  if (messages.some(m => userNotFoundPatterns.some(p => p.test(m)))) return classified(LoginErrorKind.UserNotFound);
  // A fetch that never got a response (blocked, DNS failure, CORS, offline) has no status code.
  if (networkError && statusCode === undefined) return classified(LoginErrorKind.ApiUnreachable);
  if (statusCode === 401 || messages.some(m => m.includes('Not Authenticated'))) return classified(LoginErrorKind.SignInRejected);
  if (statusCode !== undefined && statusCode >= 500) return classified(LoginErrorKind.ServerError);
  return classified(LoginErrorKind.Unknown);
};
